/**
 * `plan-vendor-import` / `execute-vendor-import` / `list-vendor-imports` —
 * CLM-113 (FR-39, DR-12, NFR-19) + CLM-114 (FR-40, FR-40a).
 *
 * Gerry: *"give me an interface to upload the XLS with vendor name, id and
 * addresses, so we can load that in the system and then identify contracts
 * against it."* An Admin uploads the Business Central "Vendor" export (`.xlsx`)
 * or a CSV of it, sees a per-row **plan** (create / update / link / skip /
 * reject, each with its reason and row reference), approves rows, and
 * **executes** — and re-uploading the same sheet writes nothing at all
 * (R-113-13). The CLM-57 shape (`upload-bundle.ts`): the plan persists nothing
 * (rule 8), execute re-sends the same bytes with the plan's digest, and any
 * change between the two is a `409`.
 *
 * ## No AI anywhere
 *
 * Nothing here calls a model or reads contract text. The one AI-adjacent effect
 * is stated on the wire: a new alias or display name on an **existing** party
 * changes the FR-29 substitution map of that party's contracts, so the receipt
 * counts them and carries the operator steps (`fr29`, R-114-6).
 *
 * ## Who
 *
 * `party:import_vendors`, Admin only, on all three endpoints (R-113-20). The
 * gate runs before the multipart body is decoded, so a non-Admin's file is never
 * parsed.
 *
 * ## What is never on the wire
 *
 * Contact data, PAN, GST/VAT or registration numbers (R-113-4): the import reads
 * `Assessee Code`, `Partner Type`, `P.A.N. No.` and `Privacy Blocked` for a check
 * only, and they appear here as **captions**, never values. A same-PAN candidate
 * carries the earlier row's reference, never the PAN (R-113-26). An individual's
 * row carries no name, alias or address (R-113-5).
 *
 * ## The transport
 *
 * Both POSTs are `multipart/form-data` with CLM-57's two server-named parts:
 * `metadata` (this module's request schema, as JSON) and `document` (the sheet's
 * bytes). `VendorSheetDescriptorSchema` describes the `document` part and is
 * verified against it server-side.
 *
 * `packages/db`'s static suite reads this file by path and asserts that the
 * batch vocabularies (`VENDOR_IMPORT_ENTITY_SOURCES`, `…_SOURCE_SYSTEMS`,
 * `…_FILE_FORMATS`, `…_INDIVIDUAL_TEST_COLUMNS`) equal the migration's CHECK
 * literals — keep each `export const X = [...] as const;` on one line.
 */
import { z } from 'zod';
import { IsoTimestampUtcSchema, PartyKeySchema, Sha256Schema, UpnSchema, VendorPartyKeySchema, } from '../primitives.js';
import { PartyTypeSchema } from '../vocabulary.js';
import { PageInfoSchema, PageRequestSchema } from '../pagination.js';
import { AuditReceiptSchema } from '../provenance.js';
import { CloudeqEntityKeySchema } from '../domain/cloudeq-entity.js';
import { IsoCountryCodeSchema, VENDOR_ADDRESS_FIELDS, VENDOR_NO_MAX_LENGTH, VendorAddressFieldSchema, VendorImportBatchIdSchema, VendorNoSchema, } from '../domain/vendor-reference.js';
/* -------------------------------------------------------------------------- */
/* Bounds — NFR-19, R-113-21                                                   */
/* -------------------------------------------------------------------------- */
const MIB = 1_048_576;
/** Data rows a sheet may carry. At the India sheet's density the default byte bound binds first. */
export const VENDOR_IMPORT_MAX_ROWS = 5000;
/** Columns (xlsx) or fields per record (CSV). */
export const VENDOR_IMPORT_MAX_COLUMNS = 256;
/** The header is the first row within rows 1–20 **by row number** (or the first 20 CSV records) that carries a vendor-number and a name caption (R-113-3). */
export const VENDOR_IMPORT_HEADER_WINDOW = 20;
/** ZIP central-directory entries, counted over the whole directory (R-113-2). */
export const VENDOR_IMPORT_MAX_PARTS = 256;
/** `<si>` elements, counted as they are read — never taken from `count` / `uniqueCount`. */
export const VENDOR_IMPORT_MAX_SHARED_STRINGS = 200_000;
/** Line breaks in a decoded CSV, counted before `parseCsv` runs (it has no cap of its own). */
export const VENDOR_IMPORT_MAX_CSV_LINE_BREAKS = 50_000;
/**
 * Rows with content either reader keeps in its grid, counted **while** the
 * sheet is read, so a sheet of a million one-cell rows is refused
 * (`too_many_rows`) before its grid exists rather than after. Twice the data-row
 * cap plus the header window: room for the preamble and for rows whose only
 * content is in ignored columns. The data-row cap itself is applied once the
 * header is found.
 */
export const VENDOR_IMPORT_MAX_SHEET_ROWS = 2 * (VENDOR_IMPORT_MAX_ROWS + VENDOR_IMPORT_HEADER_WINDOW);
/**
 * The characters of one cell either reader keeps. A longer cell is cut where it
 * is read and marked as cut, so nothing downstream — the header match, the
 * classifier's normalisers — ever handles more than this of one cell, however
 * often a shared string is referenced. Every imported field's own bound
 * (`VENDOR_SHEET_FIELD_MAX_LENGTH`, at most 200) is far below it, so a cut
 * imported cell is always `value_too_long`, and a cut `Privacy Blocked` cell
 * reads as blocked (R-113-6, the conservative direction).
 */
export const VENDOR_IMPORT_MAX_CELL_CHARS = 1024;
/** Inflated-size caps per part read, and in total (the declared size, then the actual length). */
export const VENDOR_IMPORT_MAX_SHEET_PART_BYTES = 32 * MIB;
export const VENDOR_IMPORT_MAX_SHARED_STRINGS_PART_BYTES = 16 * MIB;
export const VENDOR_IMPORT_MAX_OTHER_PART_BYTES = 1 * MIB;
export const VENDOR_IMPORT_MAX_INFLATED_TOTAL_BYTES = 64 * MIB;
/** CLM-57's declared ÷ compressed bound, per part read. */
export const VENDOR_IMPORT_MAX_INFLATE_RATIO = 100;
/** Element nesting the XML reader admits; one deeper is `xml_malformed`. */
export const VENDOR_IMPORT_MAX_XML_DEPTH = 64;
/** The first non-empty preamble cell, echoed so the Admin can see what the sheet calls itself (never stored or logged). */
export const VENDOR_IMPORT_PREAMBLE_TEXT_MAX = 160;
/** The Admin's reason for including an individual (R-113-5). */
export const VENDOR_IMPORT_INDIVIDUAL_REASON_MAX = 500;
/**
 * Cells echoed in a plan row (names, aliases, address lines, the vendor number)
 * are cut to this many characters. Every accepted value is within its stored
 * bound already; only a `value_too_long` row's value can be longer.
 */
export const VENDOR_IMPORT_ECHO_MAX_CHARS = 300;
/** Business Central's Name field lengths: a Name of exactly this many characters is flagged `name_may_be_truncated` (R-113-24). */
export const VENDOR_IMPORT_TRUNCATION_LENGTHS = [50, 100];
/* -------------------------------------------------------------------------- */
/* Formats — D-2.1-9, R-113-1                                                  */
/* -------------------------------------------------------------------------- */
export const VENDOR_IMPORT_MEDIA_TYPES = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'];
export const VendorImportMediaTypeSchema = z.enum(VENDOR_IMPORT_MEDIA_TYPES);
/** Mirrors `ck_vendor_import_batch_file_format`. */
export const VENDOR_IMPORT_FILE_FORMATS = ['xlsx', 'csv'];
export const VendorImportFileFormatSchema = z.enum(VENDOR_IMPORT_FILE_FORMATS);
/**
 * The descriptor's media type for each format. The web sets it from the file
 * extension (a browser on Windows reports `.csv` as `application/vnd.ms-excel`,
 * so `File.type` decides nothing); the server sniffs the bytes and refuses a
 * disagreement as `media_type_mismatch`.
 */
export const VENDOR_IMPORT_MEDIA_TYPE_BY_FORMAT = Object.freeze({
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    csv: 'text/csv',
});
/* -------------------------------------------------------------------------- */
/* The column allow-list — R-113-4                                             */
/* -------------------------------------------------------------------------- */
/** The imported fields — "vendor name, id and addresses", and nothing else. */
export const VENDOR_SHEET_FIELDS = [
    'vendor_no',
    'name',
    'name_2',
    'search_name',
    'address_line_1',
    'address_line_2',
    'city',
    'post_code',
    'county',
    'state_code',
    'country_code',
];
export const VendorSheetFieldSchema = z.enum(VENDOR_SHEET_FIELDS);
/**
 * Field → the captions that map to it, matched after `vendorCaptionKey`
 * (trimmed, whitespace collapsed, case-insensitive). The Business Central
 * captions come first. Two captions of one sheet mapping to one field are
 * `duplicate_column`.
 */
export const VENDOR_SHEET_COLUMNS = Object.freeze({
    vendor_no: ['No.', 'Vendor No.', 'Vendor ID', 'Vendor Number'],
    name: ['Name', 'Vendor Name'],
    name_2: ['Name 2'],
    search_name: ['Search Name'],
    address_line_1: ['Address', 'Address 1', 'Address Line 1'],
    address_line_2: ['Address 2', 'Address Line 2'],
    city: ['City', 'Town'],
    post_code: ['Post Code', 'Postcode', 'Postal Code', 'ZIP Code'],
    county: ['County'],
    state_code: ['State Code'],
    country_code: ['Country/Region Code', 'Country Code', 'Country'],
});
/**
 * The bound a raw cell of each field must meet, else the row is rejected
 * `value_too_long` (§3.4 rule 6). The stored columns' bounds: the vendor number
 * 40, a name 200 (`party_alias.alias_text`, `party.cached_display_name`), the
 * address as `VENDOR_ADDRESS_MAX_LENGTH`. `country_code` is the **raw** cell (a
 * code or a written country name), before `countryCodeFor` reduces it to ISO
 * alpha-2.
 */
export const VENDOR_SHEET_FIELD_MAX_LENGTH = Object.freeze({
    vendor_no: VENDOR_NO_MAX_LENGTH,
    name: 200,
    name_2: 200,
    search_name: 200,
    address_line_1: 200,
    address_line_2: 200,
    city: 100,
    post_code: 20,
    county: 100,
    state_code: 10,
    country_code: 100,
});
/**
 * Columns read for a check only — never stored, returned or logged as values
 * (R-113-4): the individual test (R-113-5; the PAN also drives the in-memory
 * same-PAN signal, R-113-26) and Business Central's own privacy block (R-113-6).
 */
export const VENDOR_SHEET_CHECK_FIELDS = ['assessee_code', 'partner_type', 'pan', 'privacy_blocked'];
export const VendorSheetCheckFieldSchema = z.enum(VENDOR_SHEET_CHECK_FIELDS);
export const VENDOR_SHEET_CHECK_COLUMNS = Object.freeze({
    assessee_code: ['Assessee Code'],
    partner_type: ['Partner Type'],
    pan: ['P.A.N. No.'],
    privacy_blocked: ['Privacy Blocked'],
});
/** Mirrors `ck_vendor_import_batch_individual_test_columns`: which individual-test columns a sheet carried (R-113-5). */
export const VENDOR_IMPORT_INDIVIDUAL_TEST_COLUMNS = ['assessee_code', 'pan', 'partner_type'];
export const VendorImportIndividualTestColumnSchema = z.enum(VENDOR_IMPORT_INDIVIDUAL_TEST_COLUMNS);
/** A header carrying all four is a Business Central export (`source_system 'dynamics_bc'`, R-113-10). */
export const BC_SIGNATURE_CAPTIONS = ['No.', 'Name', 'Search Name', 'Vendor Posting Group'];
/** The comparison form of a header caption: trimmed, whitespace runs collapsed, lower-cased. */
export function vendorCaptionKey(caption) {
    return caption.trim().replace(/\s+/g, ' ').toLowerCase();
}
const CAPTION_INDEX = (() => {
    const index = new Map();
    for (const field of VENDOR_SHEET_FIELDS) {
        for (const caption of VENDOR_SHEET_COLUMNS[field])
            index.set(vendorCaptionKey(caption), { kind: 'imported', field });
    }
    for (const field of VENDOR_SHEET_CHECK_FIELDS) {
        for (const caption of VENDOR_SHEET_CHECK_COLUMNS[field])
            index.set(vendorCaptionKey(caption), { kind: 'check_only', field });
    }
    return index;
})();
/**
 * Which allow-listed field a header caption names, or `null` — an ignored
 * column, returned in the plan as its caption only. Exact equality after
 * `vendorCaptionKey`: `Intrastat Partner Type` and `Pay-to Vendor No.` match
 * nothing.
 */
export function matchVendorSheetCaption(caption) {
    return CAPTION_INDEX.get(vendorCaptionKey(caption)) ?? null;
}
/* -------------------------------------------------------------------------- */
/* The batch's provenance — R-113-3, R-113-10                                  */
/* -------------------------------------------------------------------------- */
/** Mirrors `ck_vendor_import_batch_entity_source`. */
export const VENDOR_IMPORT_ENTITY_SOURCES = ['sheet_preamble', 'admin'];
export const VendorImportEntitySourceSchema = z.enum(VENDOR_IMPORT_ENTITY_SOURCES);
/** Mirrors `ck_vendor_import_batch_source_system` — the sheet's shape, provenance only, never a key. */
export const VENDOR_IMPORT_SOURCE_SYSTEMS = ['dynamics_bc', 'vendor_sheet'];
export const VendorImportSourceSystemSchema = z.enum(VENDOR_IMPORT_SOURCE_SYSTEMS);
/** The first worksheet's state; `null` for a CSV. A hidden first sheet is still read, and said so. */
export const VENDOR_IMPORT_SHEET_STATES = ['visible', 'hidden', 'very_hidden'];
export const VendorImportSheetStateSchema = z.enum(VENDOR_IMPORT_SHEET_STATES);
/* -------------------------------------------------------------------------- */
/* The row vocabulary — R-113-12, §3.4                                          */
/* -------------------------------------------------------------------------- */
/** The acceptance's vocabulary. `link` is offered, never proposed by the plan itself (R-113-8). */
export const VENDOR_IMPORT_ACTIONS = ['create', 'update', 'link', 'skip', 'reject'];
export const VendorImportActionSchema = z.enum(VENDOR_IMPORT_ACTIONS);
/** What an Admin may approve: everything but `skip` and `reject`, which write nothing. */
export const VENDOR_IMPORT_APPROVABLE_ACTIONS = ['create', 'update', 'link'];
export const VENDOR_IMPORT_SKIP_REASONS = ['unchanged', 'empty_record', 'individual', 'decision_required'];
export const VENDOR_IMPORT_REJECT_REASONS = [
    'privacy_blocked',
    'missing_vendor_number',
    'duplicate_vendor_number',
    'missing_name',
    'unusable_name',
    'value_too_long',
    'cell_error',
    'cloudeq_entity',
];
export const VENDOR_IMPORT_REASONS = [...VENDOR_IMPORT_SKIP_REASONS, ...VENDOR_IMPORT_REJECT_REASONS];
export const VendorImportReasonSchema = z.enum(VENDOR_IMPORT_REASONS);
/**
 * Informational row flags — every flag the spec names (§3.2, §3.3). Text and
 * icon in the UI, never colour alone; each has its sentence in the web's copy
 * table, so a member with no copy is a compile error there.
 */
export const VENDOR_IMPORT_FLAGS = [
    /** R-113-6 — the source now blocks a vendor CLM already holds; listed first, for a person to act on. */
    'privacy_blocked_after_import',
    /** R-113-5 — an organisation reference whose row is now an individual: its address is cleared on approval, its aliases are not. */
    'now_individual_in_sheet',
    /** R-113-14 — the Name is new to the party (a re-used or mis-keyed vendor number?): a decision, never assumed. */
    'name_changed',
    /** R-113-14 — the display name follows the sheet no longer: a person renamed it in CLM. */
    'display_name_changed_in_clm',
    /** R-113-14, R-114-2 — a name a person retired or rejected; never reactivated by an import. */
    'alias_retired_in_clm',
    /** R-113-14, R-113-17 — the party (or a link candidate) is inactive; updated, never reactivated. */
    'party_inactive',
    /** R-113-16 — a name under 5 characters; the approval must carry `confirm_short_names`. */
    'short_name_confirmation_required',
    /** R-113-17, R-114-7 — a name another party already holds; the approval must carry `accept_shared_aliases` or leave it out. */
    'alias_shared_with_other_party',
    /** R-113-17 — a name pending as a proposal on another party; never confirmed by the import. */
    'alias_pending_on_other_party',
    /** R-114-2 — a pending proposal on this row's own party with this name will be confirmed in place. */
    'alias_pending_will_confirm',
    /** R-113-18 — this vendor number is already imported under another entity. */
    'vendor_no_known_under_other_entity',
    /** R-113-24 — a Search Name that is a prefix of the Name is not imported as an alias. */
    'search_name_truncation_ignored',
    /** R-113-24 — a one-word name (it will match file-name tokens at bundle intake). */
    'single_word_alias',
    /** R-113-24 — a Name at exactly one of Business Central's field lengths. */
    'name_may_be_truncated',
    /** R-113-24 — a non-ISO country code mapped to ISO (`UK` → `GB`). */
    'country_code_normalised',
    /** R-113-24 — a country code nothing recognises: dropped, never a reason to refuse the vendor. */
    'country_code_unrecognised',
    /** R-113-26 — an earlier row of this sheet carries the same PAN: one decision, never merged. */
    'same_pan_as_earlier_row',
];
export const VendorImportFlagSchema = z.enum(VENDOR_IMPORT_FLAGS);
/** Sheet-level flags. `individual_test_weaker` needs `confirm_individual_test_weaker` on execute (R-113-5). */
export const VENDOR_IMPORT_SHEET_FLAGS = ['individual_test_weaker'];
export const VendorImportSheetFlagSchema = z.enum(VENDOR_IMPORT_SHEET_FLAGS);
/** Which column an imported name came from. The Name is an alias too (R-114-1). */
export const VENDOR_IMPORT_ALIAS_COLUMNS = ['name', 'name_2', 'search_name'];
export const VendorImportAliasColumnSchema = z.enum(VENDOR_IMPORT_ALIAS_COLUMNS);
/** What the Admin may leave out (`omit_aliases`). A Name cannot be left out (R-113-17). */
export const VENDOR_IMPORT_OMITTABLE_ALIAS_COLUMNS = ['name_2', 'search_name'];
export const VendorImportOmittableAliasColumnSchema = z.enum(VENDOR_IMPORT_OMITTABLE_ALIAS_COLUMNS);
/**
 * An imported name's status against the party the row writes, judged by
 * `aliasMatchKey` (the substituter's own tolerances, R-113-24).
 */
export const VENDOR_IMPORT_ALIAS_STATUSES = [
    'new',
    'exists',
    'exists_retired',
    'omitted',
    'pending_will_confirm',
    'shared_with_other_party',
];
export const VendorImportAliasStatusSchema = z.enum(VENDOR_IMPORT_ALIAS_STATUSES);
/** What a link candidate matched on. `sheet_row_pan` carries the earlier row's reference, never the PAN (R-113-26). */
export const VENDOR_IMPORT_LINK_MATCHES = ['display_name', 'alias', 'sheet_row', 'sheet_row_pan'];
export const VendorImportLinkMatchSchema = z.enum(VENDOR_IMPORT_LINK_MATCHES);
/* -------------------------------------------------------------------------- */
/* Refusals — R-113-1, R-113-2, R-113-11, R-113-21                              */
/* -------------------------------------------------------------------------- */
/**
 * File refusals: each a `400` whose `field_errors[0]` is `{ path: 'file',
 * message: '<refusal>: <detail>' }`, the `uploads-bundle.ts` convention, so the
 * page maps the leading token to copy. CLM-57's package names are reused, **not**
 * its statuses: `too_many_entries` and `total_too_large` are named 400s here
 * because NFR-19 asks for a named error. The byte bound alone is a bare `413
 * payload_too_large`, which can carry no field error — the page names it from
 * `limits`.
 */
export const VENDOR_IMPORT_FILE_REFUSALS = [
    // format
    'ole_container',
    'macro_enabled_workbook',
    'not_a_workbook',
    'not_utf8_csv',
    'csv_malformed',
    'media_type_mismatch',
    // package
    'too_many_entries',
    'unsafe_entry_path',
    'encrypted_entry',
    'undecodable_entry',
    'entry_too_large',
    'total_too_large',
    'inflate_ratio',
    'duplicate_part',
    // XML
    'xml_doctype_refused',
    'xml_undefined_entity',
    'xml_malformed',
    // shape
    'no_sheet',
    'header_not_found',
    'duplicate_column',
    'too_many_rows',
    'too_many_columns',
    'too_many_strings',
];
/**
 * `422` reasons — the leading token of `field_errors[0].message`, at the
 * request path it names (`cloudeq_entity_key`, `confirm_individual_test_weaker`,
 * `approved[i].<field>`). Nothing has been written when one is returned.
 */
export const VENDOR_IMPORT_UNPROCESSABLE_REASONS = [
    /** `cloudeq_entity_key` names no active entity (plan and execute). */
    'entity_unknown',
    /** `cloudeq_entity_key` differs from the entity the sheet's preamble names (plan and execute, R-113-3). */
    'entity_contradicts_sheet',
    /** The plan carries `individual_test_weaker` and the execute does not confirm it (R-113-5). */
    'individual_test_weaker_unconfirmed',
    /** An approved `row_ref` the recomputed plan does not have. */
    'row_not_in_plan',
    /** An approved action the row was not offered (R-113-12) — a rejected or empty row's included. */
    'action_not_offered',
    /** A `link` whose target is neither a candidate of the row nor an earlier approved `create` row. */
    'link_target_not_offered',
    /** A row flagged `short_name_confirmation_required` approved without `confirm_short_names` (R-113-16). */
    'short_name_unconfirmed',
    /** A row that would write a name another party holds, approved without `accept_shared_aliases` (R-113-17). */
    'shared_alias_unconfirmed',
    /** An individual's row approved without `include_individual_reason` (R-113-5). */
    'individual_reason_required',
    /** `include_individual_reason` on a row that is not an individual's. */
    'individual_reason_not_applicable',
    /** `omit_aliases` naming a column the row would not import as a new alias, or any on an individual. */
    'omit_not_offered',
];
/**
 * `409` reasons on execute, the leading token at the path named: `file_changed`
 * (`planned_sha256` — the re-sent bytes are not the planned ones), `plan_changed`
 * (`plan_digest` — the registry moved between plan and execute) and
 * `registry_changed` (a `23505` despite the shared lock — defensive only,
 * R-113-11). Each says: review the sheet again.
 */
export const VENDOR_IMPORT_CONFLICT_REASONS = ['file_changed', 'plan_changed', 'registry_changed'];
/* -------------------------------------------------------------------------- */
/* Shared shapes                                                               */
/* -------------------------------------------------------------------------- */
/** `row:N` (an Excel row number, from `<row r>`) or `line:N` (a CSV record's first physical line). Mirrors `ck_vendor_import_batch_header_row_ref`. */
export const VENDOR_IMPORT_ROW_REF_PATTERN = /^(row|line):[1-9][0-9]{0,5}$/;
export const VendorImportRowRefSchema = z.string().regex(VENDOR_IMPORT_ROW_REF_PATTERN, 'expected row:N or line:N');
const echoText = z.string().min(1).max(VENDOR_IMPORT_ECHO_MAX_CHARS);
/**
 * Descriptor of the `document` part — the `BundleDescriptorSchema` shape. The
 * media type is an enum of the two formats, so anything else is a schema `400`.
 */
export const VendorSheetDescriptorSchema = z
    .object({
    file_name: z.string().min(1).max(255),
    media_type: VendorImportMediaTypeSchema,
    size_bytes: z.number().int().min(1),
    sha256: Sha256Schema,
})
    .strict();
/**
 * NFR-19's bounds as the server applies them (R-113-21): the effective import
 * bound (`CLM_VENDOR_IMPORT_MAX_BYTES`, narrowed under the request cap less its
 * metadata reserve — `0` when that leaves nothing), the row cap, and the request
 * cap the execute body (file + approved list) must fit under. The page reads
 * them on load from `list-vendor-imports`, so it can refuse an oversized file
 * before the first upload.
 */
export const VendorImportLimitsSchema = z
    .object({
    max_bytes: z.number().int().min(0),
    max_rows: z.number().int().min(1),
    max_request_bytes: z.number().int().min(1),
})
    .refine((limits) => limits.max_bytes <= limits.max_request_bytes, 'the import bound sits under the request cap');
/** One batch, as the history and the receipt show it — `clm.vendor_import_batch`. */
export const VendorImportBatchSchema = z
    .object({
    import_batch_id: VendorImportBatchIdSchema,
    cloudeq_entity_key: CloudeqEntityKeySchema,
    entity_source: VendorImportEntitySourceSchema,
    source_system: VendorImportSourceSystemSchema,
    individual_test_columns: z.array(VendorImportIndividualTestColumnSchema),
    file_name: z.string().min(1).max(255),
    file_format: VendorImportFileFormatSchema,
    file_sha256: Sha256Schema,
    file_size_bytes: z.number().int().min(1),
    header_row_ref: VendorImportRowRefSchema,
    data_row_count: z.number().int().min(0),
    plan_digest: Sha256Schema,
    rows_created: z.number().int().min(0),
    rows_linked: z.number().int().min(0),
    rows_updated: z.number().int().min(0),
    rows_unchanged: z.number().int().min(0),
    rows_skipped: z.number().int().min(0),
    rows_rejected: z.number().int().min(0),
    aliases_added: z.number().int().min(0),
    executed_by_upn: UpnSchema,
    executed_utc: IsoTimestampUtcSchema,
})
    .superRefine((batch, ctx) => {
    const total = batch.rows_created + batch.rows_linked + batch.rows_updated + batch.rows_unchanged + batch.rows_skipped + batch.rows_rejected;
    if (total !== batch.data_row_count) {
        ctx.addIssue({ code: 'custom', path: ['data_row_count'], message: 'the six row counts account for every data row (ck_vendor_import_batch_row_total)' });
    }
    if (batch.rows_created + batch.rows_linked + batch.rows_updated < 1) {
        ctx.addIssue({ code: 'custom', path: ['rows_created'], message: 'a batch exists only for an execute that wrote something (R-113-13)' });
    }
});
/* -------------------------------------------------------------------------- */
/* Plan                                                                        */
/* -------------------------------------------------------------------------- */
export const PlanVendorImportRequestSchema = z
    .object({
    file: VendorSheetDescriptorSchema,
    /**
     * The entity the Admin picked when the preamble names none. Against a
     * detected entity it is `422 entity_contradicts_sheet` — a detected entity
     * cannot be overridden (R-113-3).
     */
    cloudeq_entity_key: CloudeqEntityKeySchema.optional(),
})
    .strict();
/** The statuses under which another holder can be named: a name the row would write onto its party. */
const SHAREABLE_STATUSES = new Set(['shared_with_other_party', 'pending_will_confirm']);
/**
 * A name the row would import, with its status against the party it writes.
 *
 * Another holder of the name (R-113-17, R-114-7) is named in one of two lists:
 * `other_party_keys`, a party the registry or an earlier row of this sheet
 * already ties to it, or `other_row_refs`, an earlier row of this sheet with no
 * party yet — the name is shared with that row's party only if that row is
 * imported as a new vendor in the same execute. A `shared_with_other_party`
 * name names at least one holder. A `pending_will_confirm` name (a pending
 * proposal on this row's own party, R-114-2) may name holders too: confirming it
 * makes the name active on two parties, so it needs the same acceptance.
 */
export const VendorImportPlanAliasSchema = z
    .object({
    text: echoText,
    column: VendorImportAliasColumnSchema,
    status: VendorImportAliasStatusSchema,
    /** Other parties that hold, or will hold, this name. */
    other_party_keys: z.array(PartyKeySchema),
    /** Earlier rows of this sheet with no party yet that would import this name too. */
    other_row_refs: z.array(VendorImportRowRefSchema),
})
    .superRefine((alias, ctx) => {
    const holders = alias.other_party_keys.length + alias.other_row_refs.length;
    if (alias.status === 'shared_with_other_party' && holders === 0) {
        ctx.addIssue({ code: 'custom', path: ['other_party_keys'], message: 'a shared name names its other holder' });
    }
    if (holders > 0 && !SHAREABLE_STATUSES.has(alias.status)) {
        ctx.addIssue({ code: 'custom', path: ['other_party_keys'], message: 'only a name the row writes can be shared' });
    }
});
/** The address a row would write, as tidied; `country_code` after `countryCodeFor` (`null` when unrecognised). */
export const VendorImportPlanAddressSchema = z
    .object({
    address_line_1: echoText.nullable(),
    address_line_2: echoText.nullable(),
    city: echoText.nullable(),
    post_code: echoText.nullable(),
    county: echoText.nullable(),
    state_code: echoText.nullable(),
    country_code: IsoCountryCodeSchema.nullable(),
})
    .refine((address) => VENDOR_ADDRESS_FIELDS.some((field) => address[field] !== null), 'an address with every field empty is null, not an object');
/** The Vendor party an existing reference points at (§3.4 rule 9). */
export const VendorImportExistingPartySchema = z.object({
    party_key: VendorPartyKeySchema,
    display_name: z.string().min(1).max(200),
    is_active: z.boolean(),
});
/**
 * A party or an earlier row this row may be the same vendor as. Client and
 * Partner matches are listed too, so the Admin sees them — but only a Vendor
 * candidate (or an earlier row) can be a `link` target (R-113-8, R-113-17).
 */
export const VendorImportLinkCandidateSchema = z
    .object({
    /** The party's key; for an earlier row, its party when it already has a reference, else `null`. */
    party_key: PartyKeySchema.nullable(),
    /**
     * The party's display name, or the earlier row's Name. `null` exactly on an
     * individual's row (R-113-5): a candidate matched by name is named like the
     * person, so the row shows the key or the row reference only.
     */
    display_name: echoText.nullable(),
    party_type: PartyTypeSchema,
    matched_on: VendorImportLinkMatchSchema,
    /** The earlier row, for `sheet_row` / `sheet_row_pan`; `null` for a party match. */
    row_ref: VendorImportRowRefSchema.nullable(),
    is_active: z.boolean(),
    /**
     * R-114-2 — this Vendor party holds a **pending** proposal of a name the row
     * would write: a `link` to it confirms that proposal in place (a `create`
     * leaves it pending, flagged `alias_pending_on_other_party`).
     */
    confirms_pending_alias: z.boolean(),
})
    .superRefine((candidate, ctx) => {
    const sheetRow = candidate.matched_on === 'sheet_row' || candidate.matched_on === 'sheet_row_pan';
    if (sheetRow && (candidate.row_ref === null || candidate.party_type !== 'Vendor')) {
        ctx.addIssue({ code: 'custom', path: ['row_ref'], message: 'an earlier-row candidate names its row and is a Vendor' });
    }
    if (!sheetRow && (candidate.party_key === null || candidate.row_ref !== null)) {
        ctx.addIssue({ code: 'custom', path: ['party_key'], message: 'a party candidate names its party and no row' });
    }
    if (candidate.confirms_pending_alias && (candidate.party_key === null || candidate.party_type !== 'Vendor')) {
        ctx.addIssue({ code: 'custom', path: ['confirms_pending_alias'], message: 'only an existing Vendor party can hold a proposal a link confirms' });
    }
});
const SKIP_REASONS = new Set(VENDOR_IMPORT_SKIP_REASONS);
const REJECT_REASONS = new Set(VENDOR_IMPORT_REJECT_REASONS);
const hasDuplicates = (values) => new Set(values).size !== values.length;
/**
 * One data row of the sheet and what executing it would do (R-113-12). The
 * first §3.4 rule that matches decides `proposed_action` and its one reason;
 * `flags` accumulate. A row that needs a decision is `skip` +
 * `decision_required` until the Admin decides, and an undecided row writes
 * nothing.
 */
export const VendorImportPlanRowSchema = z
    .object({
    row_ref: VendorImportRowRefSchema,
    /** Trimmed and upper-cased; `null` when blank (`missing_vendor_number`). */
    vendor_no: echoText.nullable(),
    /** R-113-5 — decided on every row, whatever its action. */
    is_individual: z.boolean(),
    proposed_action: VendorImportActionSchema,
    reasons: z.array(VendorImportReasonSchema),
    flags: z.array(VendorImportFlagSchema),
    offered_actions: z.array(VendorImportActionSchema),
    /** The tidied Name; `null` for an individual (R-113-5) and for a blank Name. */
    display_name: echoText.nullable(),
    /** Every name the row would import, the Name first; `null` for an individual. */
    aliases: z.array(VendorImportPlanAliasSchema).max(VENDOR_IMPORT_ALIAS_COLUMNS.length).nullable(),
    /** `null` for an individual and for a row with no address. */
    address: VendorImportPlanAddressSchema.nullable(),
    /** On an update, the address fields that change. */
    address_changes: z.array(VendorAddressFieldSchema),
    existing_party: VendorImportExistingPartySchema.nullable(),
    link_candidates: z.array(VendorImportLinkCandidateSchema),
    /**
     * The key a `create` would mint — a provisional preview, outside
     * `plan_digest`: it is minted at execute in sheet order and can move when an
     * earlier undecided row is approved as `create`. The receipt reports the
     * real key.
     */
    key_preview: VendorPartyKeySchema.nullable(),
    /** In the caller's scope, the contracts whose FR-29 map changes if this row is approved (R-114-6). Outside `plan_digest`. */
    affected_contracts_in_scope: z.number().int().min(0),
    /**
     * R-114-6 — approving this row would change the names of an existing party
     * that has **any** `contract_party` row (the A7 existence test, unscoped,
     * party keys only). The plan's FR-29 notice follows this, never the scoped
     * count alone, so a scope cannot hide it. Outside `plan_digest`.
     */
    affects_parties_with_contracts: z.boolean(),
})
    .superRefine((row, ctx) => {
    if (row.proposed_action === 'reject') {
        if (row.reasons.length === 0 || !row.reasons.every((reason) => REJECT_REASONS.has(reason))) {
            ctx.addIssue({ code: 'custom', path: ['reasons'], message: 'a rejected row carries reject reasons only' });
        }
        if (row.offered_actions.length > 0) {
            ctx.addIssue({ code: 'custom', path: ['offered_actions'], message: 'a rejected row offers nothing — it cannot be included' });
        }
    }
    else if (row.proposed_action === 'skip') {
        if (row.reasons.length !== 1 || !SKIP_REASONS.has(row.reasons[0])) {
            ctx.addIssue({ code: 'custom', path: ['reasons'], message: 'a skipped row carries exactly one skip reason' });
        }
    }
    else {
        if (row.reasons.length > 0) {
            ctx.addIssue({ code: 'custom', path: ['reasons'], message: 'a row that writes carries no reason' });
        }
        if (!row.offered_actions.includes(row.proposed_action)) {
            ctx.addIssue({ code: 'custom', path: ['offered_actions'], message: 'the proposed action is among those offered' });
        }
    }
    if (row.offered_actions.includes('reject') || hasDuplicates(row.offered_actions)) {
        ctx.addIssue({ code: 'custom', path: ['offered_actions'], message: 'offered actions are distinct, and never reject' });
    }
    if (hasDuplicates(row.flags) || hasDuplicates(row.reasons) || hasDuplicates(row.address_changes)) {
        ctx.addIssue({ code: 'custom', path: ['flags'], message: 'flags, reasons and address changes are each distinct' });
    }
    if (row.is_individual && (row.display_name !== null || row.aliases !== null || row.address !== null)) {
        ctx.addIssue({ code: 'custom', path: ['display_name'], message: 'an individual’s row carries no name, alias or address (R-113-5)' });
    }
    if (row.link_candidates.some((candidate) => (candidate.display_name === null) !== row.is_individual)) {
        ctx.addIssue({
            code: 'custom',
            path: ['link_candidates'],
            message: 'a candidate is named exactly on an organisation’s row — on an individual’s it would name the person (R-113-5)',
        });
    }
    if (row.key_preview !== null && !row.offered_actions.includes('create')) {
        ctx.addIssue({ code: 'custom', path: ['key_preview'], message: 'a key is previewed only for a row offered create' });
    }
});
/**
 * A reference for this entity the sheet no longer has — listed, never
 * deactivated (D-2.1-10, R-113-15). An included individual's reference is listed
 * by its key and vendor number only: its party is named after the person, and
 * the plan names no individual (R-113-5).
 */
export const VendorImportNotInSheetSchema = z
    .object({
    party_key: VendorPartyKeySchema,
    is_individual: z.boolean(),
    /** The party's display name; `null` exactly for an individual's reference. */
    display_name: z.string().min(1).max(200).nullable(),
    vendor_no: VendorNoSchema,
    last_import_batch_id: VendorImportBatchIdSchema,
})
    .refine((entry) => (entry.display_name === null) === entry.is_individual, {
    path: ['display_name'],
    message: 'an individual’s reference is listed without a name, and only an individual’s (R-113-5)',
});
/** The summary tiles. Every data row is in exactly one of the eight row tiles. */
export const VendorImportPlanTotalsSchema = z.object({
    data_rows: z.number().int().min(0),
    create: z.number().int().min(0),
    update: z.number().int().min(0),
    link: z.number().int().min(0),
    unchanged: z.number().int().min(0),
    decision_required: z.number().int().min(0),
    /** Not imported: personal data (R-113-5). */
    individual: z.number().int().min(0),
    empty_record: z.number().int().min(0),
    rejected: z.number().int().min(0),
    not_in_sheet: z.number().int().min(0),
});
/** The tile a row counts under — the one rule the plan's totals and the page's filter share. */
export function vendorImportTileOf(row) {
    if (row.proposed_action === 'reject')
        return 'rejected';
    if (row.proposed_action !== 'skip')
        return row.proposed_action;
    const reason = row.reasons[0];
    return reason === 'unchanged' || reason === 'empty_record' || reason === 'individual' ? reason : 'decision_required';
}
export const PlanVendorImportResponseSchema = z
    .object({
    file: z
        .object({
        file_name: z.string().min(1).max(255),
        format: VendorImportFileFormatSchema,
        sha256: Sha256Schema,
        size_bytes: z.number().int().min(1),
        /** The first worksheet's name; `null` for a CSV. */
        sheet_name: z.string().min(1).max(255).nullable(),
        sheet_state: VendorImportSheetStateSchema.nullable(),
        header_row_ref: VendorImportRowRefSchema,
        data_row_count: z.number().int().min(0).max(VENDOR_IMPORT_MAX_ROWS),
        /** Worksheets after the first, which are never read. */
        other_sheets_ignored: z.number().int().min(0),
    })
        .superRefine((file, ctx) => {
        const csv = file.format === 'csv';
        const shapeAgrees = csv
            ? file.sheet_name === null && file.sheet_state === null && file.other_sheets_ignored === 0 && file.header_row_ref.startsWith('line:')
            : file.sheet_name !== null && file.sheet_state !== null && file.header_row_ref.startsWith('row:');
        if (!shapeAgrees) {
            ctx.addIssue({ code: 'custom', path: ['format'], message: 'a CSV has no sheet and counts lines; a workbook names its sheet and counts rows' });
        }
    }),
    source_system: VendorImportSourceSystemSchema,
    /** Which individual-test columns the sheet carries (R-113-5). */
    individual_test_columns: z.array(VendorImportIndividualTestColumnSchema),
    sheet_flags: z.array(VendorImportSheetFlagSchema),
    entity: z
        .object({
        /** The entity the preamble names by exact equality, or `null`. */
        detected_key: CloudeqEntityKeySchema.nullable(),
        /** The first non-empty preamble cell (cloudEQ's own name); never stored or logged. */
        preamble_text: z.string().min(1).max(VENDOR_IMPORT_PREAMBLE_TEXT_MAX).nullable(),
        /** The entity this plan is for: the detected one, else the Admin's pick, else `null`. */
        chosen_key: CloudeqEntityKeySchema.nullable(),
        /** No entity yet: the Admin must pick one and plan again. `rows` is then empty. */
        required: z.boolean(),
    })
        .superRefine((entity, ctx) => {
        if (entity.required !== (entity.chosen_key === null)) {
            ctx.addIssue({ code: 'custom', path: ['required'], message: 'an entity is required exactly when none is chosen' });
        }
        if (entity.detected_key !== null && entity.chosen_key !== entity.detected_key) {
            ctx.addIssue({ code: 'custom', path: ['chosen_key'], message: 'a detected entity cannot be overridden (R-113-3)' });
        }
    }),
    /** The Columns panel: each imported field beside its caption, the check-only captions, the ignored captions. Never a value. */
    columns: z.object({
        mapped: z.array(z.object({ field: VendorSheetFieldSchema, caption: z.string().min(1).max(VENDOR_IMPORT_ECHO_MAX_CHARS) })),
        checked_only: z.array(z.string().min(1).max(VENDOR_IMPORT_ECHO_MAX_CHARS)),
        ignored: z.array(z.string().min(1).max(VENDOR_IMPORT_ECHO_MAX_CHARS)).max(VENDOR_IMPORT_MAX_COLUMNS),
    }),
    rows: z.array(VendorImportPlanRowSchema).max(VENDOR_IMPORT_MAX_ROWS),
    not_in_sheet: z.array(VendorImportNotInSheetSchema),
    totals: VendorImportPlanTotalsSchema,
    limits: VendorImportLimitsSchema,
    /** R-113-11 — echoed on execute and compared with the plan recomputed there. `null` while no entity is chosen. */
    plan_digest: Sha256Schema.nullable(),
})
    .superRefine((plan, ctx) => {
    if (plan.entity.required) {
        if (plan.rows.length > 0 || plan.not_in_sheet.length > 0 || plan.plan_digest !== null) {
            ctx.addIssue({ code: 'custom', path: ['rows'], message: 'with no entity chosen there are no rows, no references and no digest' });
        }
    }
    else {
        if (plan.plan_digest === null) {
            ctx.addIssue({ code: 'custom', path: ['plan_digest'], message: 'a plan for an entity carries its digest' });
        }
        if (plan.rows.length !== plan.file.data_row_count) {
            ctx.addIssue({ code: 'custom', path: ['rows'], message: 'one plan row per data row' });
        }
        const counted = { create: 0, update: 0, link: 0, unchanged: 0, decision_required: 0, individual: 0, empty_record: 0, rejected: 0 };
        for (const row of plan.rows)
            counted[vendorImportTileOf(row)] += 1;
        for (const [tile, count] of Object.entries(counted)) {
            if (plan.totals[tile] !== count) {
                ctx.addIssue({ code: 'custom', path: ['totals', tile], message: 'a tile counts the rows it names' });
            }
        }
    }
    if (plan.totals.data_rows !== plan.file.data_row_count || plan.totals.not_in_sheet !== plan.not_in_sheet.length) {
        ctx.addIssue({ code: 'custom', path: ['totals'], message: 'data_rows and not_in_sheet count what the plan carries' });
    }
    const refs = plan.rows.map((row) => row.row_ref);
    if (hasDuplicates(refs)) {
        ctx.addIssue({ code: 'custom', path: ['rows'], message: 'each row reference appears once' });
    }
    if (hasDuplicates(plan.columns.mapped.map((column) => column.field))) {
        ctx.addIssue({ code: 'custom', path: ['columns', 'mapped'], message: 'two captions mapping to one field is a duplicate_column refusal, never a plan' });
    }
});
/* -------------------------------------------------------------------------- */
/* Execute                                                                     */
/* -------------------------------------------------------------------------- */
const IndividualReasonSchema = z
    .string()
    .min(1)
    .max(VENDOR_IMPORT_INDIVIDUAL_REASON_MAX)
    .refine((value) => value.trim().length > 0, 'a reason is never blank');
/**
 * What every approved row may carry. Each confirmation is the Admin's answer
 * to a flag the plan raised; the server re-checks every one against the plan it
 * recomputes (`422` with the row's path otherwise).
 */
const approvedRowFields = {
    row_ref: VendorImportRowRefSchema,
    /** R-113-16 — the row's short names are confirmed (they are inserted confirmed by the Admin). */
    confirm_short_names: z.boolean().optional(),
    /** R-113-17 / R-114-7 — keep a name another party holds on both parties. */
    accept_shared_aliases: z.boolean().optional(),
    /** R-113-5 — include an individual: "a contract with this person exists or is expected". Name only is kept. */
    include_individual_reason: IndividualReasonSchema.optional(),
    /** R-113-14, R-113-24 — leave out the row's Name 2 / Search Name alias; recorded, never re-proposed. */
    omit_aliases: z
        .array(VendorImportOmittableAliasColumnSchema)
        .max(VENDOR_IMPORT_OMITTABLE_ALIAS_COLUMNS.length)
        .refine((columns) => !hasDuplicates(columns), 'each column is left out once')
        .optional(),
};
/** An approved row, discriminated on the action. A `link` names a party or an earlier row of this sheet, never both. */
export const VendorImportApprovedRowSchema = z.discriminatedUnion('action', [
    z.object({ action: z.literal('create'), ...approvedRowFields }).strict(),
    z.object({ action: z.literal('update'), ...approvedRowFields }).strict(),
    z
        .object({
        action: z.literal('link'),
        ...approvedRowFields,
        /** A Vendor candidate of the row (R-113-8: a link target is always a Vendor). */
        party_key: VendorPartyKeySchema.optional(),
        /** An earlier row of this sheet approved as `create` in the same execute. */
        same_as_row_ref: VendorImportRowRefSchema.optional(),
    })
        .strict()
        .refine((row) => (row.party_key === undefined) !== (row.same_as_row_ref === undefined), {
        message: 'a link names exactly one of party_key or same_as_row_ref',
    }),
]);
export const ExecuteVendorImportRequestSchema = z
    .object({
    file: VendorSheetDescriptorSchema,
    /** The plan's `file.sha256`. The re-sent bytes' digest is compared with it: `409 file_changed` (R-113-11). */
    planned_sha256: Sha256Schema,
    cloudeq_entity_key: CloudeqEntityKeySchema,
    plan_digest: Sha256Schema,
    /** Required when the plan carried `individual_test_weaker` (R-113-5). */
    confirm_individual_test_weaker: z.boolean().optional(),
    /** May be empty: nothing approved writes nothing and returns `batch: null` (R-113-13). */
    approved: z.array(VendorImportApprovedRowSchema).max(VENDOR_IMPORT_MAX_ROWS),
})
    .strict()
    .refine((body) => !hasDuplicates(body.approved.map((row) => row.row_ref)), {
    path: ['approved'],
    message: 'each row is approved once',
});
/** Mirrors the batch's six counters: every data row has exactly one outcome. */
export const VENDOR_IMPORT_ROW_OUTCOMES = ['created', 'linked', 'updated', 'unchanged', 'skipped', 'rejected'];
export const VendorImportRowOutcomeSchema = z.enum(VENDOR_IMPORT_ROW_OUTCOMES);
const WRITING_OUTCOMES = new Set(['created', 'linked', 'updated']);
export const VendorImportRowResultSchema = z
    .object({
    row_ref: VendorImportRowRefSchema,
    outcome: VendorImportRowOutcomeSchema,
    /** The party written — the real minted key for `created`; the existing party for `unchanged`; else `null`. */
    party_key: VendorPartyKeySchema.nullable(),
    aliases_added: z.number().int().min(0),
    /** R-114-7 — each name this row added that another party also holds. */
    shared_aliases: z.array(z.object({ text: z.string().min(1).max(200), other_party_key: PartyKeySchema })),
})
    .superRefine((row, ctx) => {
    const writes = WRITING_OUTCOMES.has(row.outcome);
    if (writes && row.party_key === null) {
        ctx.addIssue({ code: 'custom', path: ['party_key'], message: 'a row that wrote names its party' });
    }
    if (!writes && (row.aliases_added > 0 || row.shared_aliases.length > 0)) {
        ctx.addIssue({ code: 'custom', path: ['aliases_added'], message: 'a row that wrote nothing added no alias' });
    }
});
/**
 * R-114-6 — the FR-29 effect, stated and counted. The notice is driven by
 * `parties_with_contracts_and_name_changes` (unscoped, party keys only), never
 * by the scoped contract count, so a practice-scoped Admin is never told "no
 * map changes" while out-of-scope contracts drift. `operator_steps` is the
 * CLM-100 refresh sequence and is non-empty exactly when that count is; the
 * import starts none of the steps.
 */
export const VendorImportFr29EffectSchema = z
    .object({
    existing_parties_with_name_changes: z.number().int().min(0),
    parties_with_contracts_and_name_changes: z.number().int().min(0),
    affected_contracts_in_scope: z.number().int().min(0),
    operator_steps: z.array(z.string().min(1).max(500)),
})
    .superRefine((effect, ctx) => {
    if (effect.parties_with_contracts_and_name_changes > effect.existing_parties_with_name_changes) {
        ctx.addIssue({ code: 'custom', path: ['parties_with_contracts_and_name_changes'], message: 'a subset of the existing parties whose names changed' });
    }
    if ((effect.operator_steps.length > 0) !== (effect.parties_with_contracts_and_name_changes > 0)) {
        ctx.addIssue({ code: 'custom', path: ['operator_steps'], message: 'operator steps exactly when a party with a contract changed its names (R-114-6)' });
    }
});
export const ExecuteVendorImportResponseSchema = z
    .object({
    /** `null` when nothing approved wrote anything: no batch row, party, alias or reference (R-113-13). */
    batch: VendorImportBatchSchema.nullable(),
    rows: z.array(VendorImportRowResultSchema).max(VENDOR_IMPORT_MAX_ROWS),
    fr29: VendorImportFr29EffectSchema,
    /** The batch row's `0046` receipt — `null` exactly when `batch` is. */
    audit: AuditReceiptSchema.nullable(),
})
    .superRefine((receipt, ctx) => {
    if ((receipt.batch === null) !== (receipt.audit === null)) {
        ctx.addIssue({ code: 'custom', path: ['audit'], message: 'the receipt is the batch row’s, so both or neither' });
    }
    if (hasDuplicates(receipt.rows.map((row) => row.row_ref))) {
        ctx.addIssue({ code: 'custom', path: ['rows'], message: 'each row reference appears once' });
    }
    const batch = receipt.batch;
    if (batch === null) {
        if (receipt.rows.some((row) => WRITING_OUTCOMES.has(row.outcome))) {
            ctx.addIssue({ code: 'custom', path: ['batch'], message: 'a row that wrote is always in a batch' });
        }
        return;
    }
    const counted = { created: 0, linked: 0, updated: 0, unchanged: 0, skipped: 0, rejected: 0 };
    for (const row of receipt.rows)
        counted[row.outcome] += 1;
    const agrees = receipt.rows.length === batch.data_row_count &&
        counted.created === batch.rows_created &&
        counted.linked === batch.rows_linked &&
        counted.updated === batch.rows_updated &&
        counted.unchanged === batch.rows_unchanged &&
        counted.skipped === batch.rows_skipped &&
        counted.rejected === batch.rows_rejected &&
        receipt.rows.reduce((sum, row) => sum + row.aliases_added, 0) === batch.aliases_added;
    if (!agrees) {
        ctx.addIssue({ code: 'custom', path: ['rows'], message: 'the per-row outcomes are the batch’s counts' });
    }
});
/* -------------------------------------------------------------------------- */
/* List                                                                        */
/* -------------------------------------------------------------------------- */
/** The history, newest first, keyset-paged; optionally one entity's batches. */
export const ListVendorImportsRequestSchema = z
    .object({
    cloudeq_entity_key: CloudeqEntityKeySchema.optional(),
    page: PageRequestSchema.optional(),
})
    .strict();
export const ListVendorImportsResponseSchema = z.object({
    batches: z.array(VendorImportBatchSchema),
    page: PageInfoSchema,
    /** Read on load, so an oversized file is refused before it is sent (R-113-21). */
    limits: VendorImportLimitsSchema,
});
//# sourceMappingURL=vendor-import.js.map